import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { humanize, titleize, unescapeHTML } from 'underscore.string';
import moment from 'moment-timezone';
import {
  isDrayageOrRailShipment,
  isDrayageShipment,
  isIntermodalShipmentType,
} from 'helpers/shipment-types';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import Accessorials from 'components/modules/shipments/Accessorials';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@cargomatic/ui';
import Node from 'components/modules/shipments/stops/Node';
import { getLatestStatus, shipperHasTag } from 'utils/formatters/shipments';
import { getDisplayAction } from 'utils/formatters/stops';
import CircleButton from 'components/v2/buttons/CircleButton';
import SessionStore from 'stores/SessionStore';
import DescriptionListItem from 'components/v2/DescriptionListItem';
import InfoIcon from 'components/v2/icons/InfoIcon';
import RefreshIcon from 'components/v2/icons/RefreshIcon';
import { calculatePalletSpaces } from 'helpers/shipments';
import { getTimezone } from 'helpers/stops';
import { Translation, withTranslation } from 'react-i18next';
import { sluggify } from 'utils/localization/utils';
import { getEquipmentBadgeColor, trailerExists } from 'helpers/equipment';
import { t } from 'i18next';
import { Disclosure } from '@headlessui/react';
import { Documents } from '../../documents/Documents';
import StopAppointmentWindow from './StopAppointmentWindow';
import {
  LoadDimensionFormatter,
  LoadUnitFormatter,
} from '../../master/formatters';
import Tooltip from 'components/ui/Tooltip';
import { PencilIcon } from 'lucide-react';
import { Timeline, TimelineDot, TimelineEvent } from 'components/ui/Timeline';
import phoneFormatter from 'phone-formatter';

function filterTimelineItems(items) {
  const canceled = items.find((item) => item.status.slug === 'canceled');
  if (canceled) {
    return [canceled];
  }
  if (items.length > 1) {
    return items.filter((item) => item.status.slug !== 'unstarted');
  }
  return items;
}

const TOOLTIPS = {
  accessorials: (
    <Tooltip id="tooltip-route-details">
      <Translation>{(t) => t('STOP_ACCESSORIALS_TOOLTIP')}</Translation>
    </Tooltip>
  ),
  references: (
    <Tooltip id="tooltip-route-details">
      <Translation>{(t) => t('REF_NUMS_NOTE')}</Translation>
    </Tooltip>
  ),
  documents: (
    <Tooltip id="tooltip-route-details">
      <Translation>{(t) => t('STOP_DOCUMENTS_TOOLTIP')}</Translation>
    </Tooltip>
  ),
};

const getStopTooltip = (text) => {
  return <Tooltip id="tooltip-stop-audit-source">{humanize(text)}</Tooltip>;
};

const getStopName = (location) => {
  if (location && location.length > 0) {
    return unescapeHTML(location[0].name);
  }

  return null;
};

const getStopAddress = (location) => {
  let address = '';

  if (location && location.length > 0) {
    address += location[0].address;

    if (location[0].suite_or_apt) {
      address += ', Suite/Apt: ' + location[0].suite_or_apt;
    }
  }

  return address;
};

const getContact = (contact) => {
  if (!_.isEmpty(contact)) {
    const contactName = humanize(contact.name || 'Not Specified');
    if (contact.phone) {
      let phoneNumber = contact.phone.trim();
      phoneNumber = phoneNumber.replace(/\D/g, ''); // Normalizing the removal spaces and characters for formatting purposes
      if (phoneNumber.charAt(0) !== '1')
        // Normalizing adding country code when it's left off of complete number
        phoneNumber = '1' + phoneNumber;
      if (phoneNumber.length < 11) {
        // Default to the number in this cases. 11 corresponds to 1-234-567-8910 (Puncuation removed)
        return `${contactName}, ${contact.phone}`;
      }

      // Otherwise the number has an extension presumably
      const number = phoneNumber.substring(0, 11);
      const extension = phoneNumber.substring(11, phoneNumber.length);
      const formattedNumber = phoneFormatter.format(number, '(NNN) NNN-NNNN');
      if (extension) {
        return `${contactName}, ${formattedNumber} (${extension})`;
      }
      return `${contactName}, ${formattedNumber}`;
    }
    return contactName;
  }
  return null;
};

const shouldEnterProvideDocumentation = (shipment, stop) => {
  const { complete, off_site, missing_docs, empty } = stop;
  // SSR - This should be simplified to just checking completion and missing docs once we go live
  if (
    !featureEnabled(CONFIGURED_FEATURES.SSR) &&
    complete &&
    off_site === false &&
    missing_docs &&
    !empty
  ) {
    return 'Provide documentation';
  }
  // SSR - This should be the case used when we go live
  if (featureEnabled(CONFIGURED_FEATURES.SSR) && complete && missing_docs) {
    return 'Provide documentation';
  }
  return null;
};

const shouldAddEquipment = (shipment, stop) => {
  if (isDrayageShipment(shipment) && !stop.complete && !stop.canceled) {
    const {
      equipments: { length },
      equipments,
    } = stop;

    const isWithoutEquipment = length === 0;
    let hasTrailer = false;
    let hasContainer = false;
    if (!isWithoutEquipment) {
      hasTrailer = trailerExists(equipments);
      hasContainer = _.find(equipments, (equipment) => {
        const { _equipmentType } = equipment;
        return _equipmentType === 'container';
      });
    }
    const hasEquipment = hasTrailer || hasContainer;
    return isWithoutEquipment || !hasEquipment;
  }
  return false;
};

const hasNotArrived = (latestTimelineObject, shipper, buttonState, onClick) => {
  const arrivedStatus = 40;
  const reasonRequired =
    shipper.requires_late_early_reason_code ||
    shipper.requires_on_time_reason_code ||
    shipper.requires_arrival_info;
  if (
    latestTimelineObject.status &&
    latestTimelineObject.status.code < arrivedStatus &&
    reasonRequired
  ) {
    return (
      <Button
        disabled={buttonState}
        onClick={() => onClick()}
        className="w-full bg-green-cm hover:bg-green-cm"
      >
        {t('ARRIVED')}
      </Button>
    );
  }
  return null;
};

const hasMetaDate = (timeline) => timeline.meta && timeline.meta.date;

class Stop extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hoveredStop: null,
      hoveredType: null,
      documentsRefetch: null,
    };
  }

  setDocumentsRefetchRef = (documentsRefetch) => {
    this.setState({
      documentsRefetch,
    });
  };

  hasShorthaulInvoicingData(props) {
    return (
      props.shipment &&
      props.shipment.shipment_type === 'shorthaul' &&
      (props.shipment.payout_exists === true ||
        props.shipment.invoice_exists === true)
    );
  }

  onMouseOver(hoveredStop, hoveredType) {
    this.setState({ hoveredStop, hoveredType });
  }

  onMouseOut() {
    this.setState({ hoveredStop: null, hoveredType: null });
  }

  getLoadPreview(shipment) {
    let totalPalletSpaces;
    const { t } = this.props;
    const { loads } = this.props.stop;

    if (shipment.loads && shipment.loads.length) {
      totalPalletSpaces = calculatePalletSpaces(shipment.loads);
    }

    const previewComponent = [];

    if (this.props.stop.full_truckload) {
      previewComponent.push(
        <Badge key="ltl" className="mb-4">
          {t('FULL_TRUCKLOAD')}
        </Badge>,
      );
    }

    const showTotalWeight = this.props.showTotalWeight;
    const shipmentWeightUnit = this.props.shipment.weight_units;
    if (showTotalWeight) {
      previewComponent.push(
        <DescriptionListItem
          key="weight"
          label={t('TOTAL_WEIGHT')}
          className="mb-2"
        >
          <LoadUnitFormatter
            weight={shipment.weight}
            weight_units={shipmentWeightUnit}
          />
        </DescriptionListItem>,
      );
    }

    _.each(this.props.stop.equipments, (equipment) => {
      const { _equipmentType } = equipment;

      if (!_equipmentType || !_equipmentType.length) {
        return null;
      }

      let badgeColor = getEquipmentBadgeColor(equipment);
      const badgeText = titleize(t(sluggify(_equipmentType)));
      previewComponent.push(
        <div key={equipment._id} className="flex items-center mb-2 space-x-2">
          <Button
            onClick={() => this.handleEquipmentClick(equipment)}
            className="text-blue-600 hover:text-blue-700 px-0"
            variant="link"
          >
            {`${equipment.equipment_id}`}
          </Button>
          <Badge color={badgeColor}>{badgeText}</Badge>
        </div>,
      );
    });

    // We don't display load units/quantities for intermodal shipments
    if (!isIntermodalShipmentType(shipment)) {
      _.each(loads, (load) => {
        // Full truckload
        if (load.quantity < 999) {
          previewComponent.push(
            <div key={load.type} className="mb-2">
              <LoadDimensionFormatter
                quantity={load.quantity}
                type={load.type}
                length={load.length}
                width={load.width}
                height={load.height}
                dimensional_units={load.dimensional_units}
              />
            </div>,
          );
        }
      });
    }

    if (
      loads &&
      loads.length > 1 &&
      totalPalletSpaces &&
      totalPalletSpaces > 0 &&
      SessionStore.isAdmin()
    ) {
      previewComponent.push(
        <div key="total-pallet-spaces">
          {t('TOTAL_PALLET_SPACE_WITH_VALUE', {
            totalPalletSpace: totalPalletSpaces,
          })}
        </div>,
      );
    }

    return previewComponent;
  }

  getNextAction() {
    const { t, shipment, stop, completeActionDisabled } = this.props;
    const latest = getLatestStatus(stop);
    const hasArrivalInfoTag =
      shipment.shipper &&
      shipment.shipper.tags.includes('requires_arrival_info');

    const hasDrayageTrailerMovesTag = shipperHasTag(
      shipment,
      'drayage_trailer_moves',
      shipment.shipment_type === 'drayage',
    );

    const hasTrailer = trailerExists(stop.equipments || []);

    const arrival =
      hasArrivalInfoTag &&
      hasNotArrived(
        latest,
        shipment.shipper,
        completeActionDisabled,
        this.handleStopArrived,
      );

    if (arrival) return arrival;
    const {
      stop: { sequence },
    } = this.props;

    let {
      stop: { action },
    } = this.props;
    const documentationRequired = shouldEnterProvideDocumentation(
      shipment,
      stop,
    );
    const showAddEquipButton = shouldAddEquipment(shipment, stop);
    if (showAddEquipButton) {
      action = 'Add Equipment';
    }

    if (action === 'deliver') {
      action = 'delivery';
    }
    const actionText = documentationRequired || humanize(action);
    const shouldHaveTrailer = hasDrayageTrailerMovesTag && !hasTrailer;

    return (
      <Button
        className="w-full bg-green-cm hover:bg-green-cm"
        id={`${action}_stop_${sequence}`}
        disabled={
          !showAddEquipButton && (completeActionDisabled || shouldHaveTrailer)
        }
        onClick={
          showAddEquipButton
            ? () => this.handleEquipmentClick()
            : () => this.handleStopComplete()
        }
      >
        {titleize(t(sluggify(actionText)))}
      </Button>
    );
  }

  handleStopArrived = () => {
    this.props.onShowStopArrivedModal(this.props.stop);
  };

  handleStopComplete() {
    this.props.onShowStopCompleteModal(this.props.stop);
  }

  handleEquipmentClick = () => {
    this.props.onShowEditEquipmentModal(this.props.stop);
  };

  showEditIcon(sequence, type) {
    return (
      this.state.hoveredStop === sequence && this.state.hoveredType === type
    );
  }

  renderAppointmentWindow() {
    const {
      stop,
      shipment: { shipment_type },
    } = this.props;

    return (
      <div
        className="col-span-2"
        onMouseEnter={() => this.onMouseOver(stop.sequence, 'windows')}
        onMouseLeave={() => this.onMouseOut()}
      >
        <StopAppointmentWindow
          stop={stop}
          shipment_type={shipment_type}
          editable
          onEditAppointmentWindow={() => {
            this.props.onShowEditTimeWindowModal(stop);
          }}
        />
      </div>
    );
  }

  renderTimeline() {
    const {
      stop: { timeline },
      t,
    } = this.props;

    const flattened = Object.keys(timeline).map((key) => ({
      key,
      ...timeline[key],
    }));
    const sortedTimeline = _.sortBy(flattened, (t) => t?.status?.code);

    const items = sortedTimeline.map((timelineItem, i) => (
      <TableRow key={i}>
        <TableCell>{timelineItem.status.message}</TableCell>
        <TableCell>
          {moment
            .tz(timelineItem.at, getTimezone(this.props.stop))
            .format('L LT z')}
        </TableCell>
        <TableCell>
          {hasMetaDate(timelineItem)
            ? moment
              .tz(timelineItem.meta.date, getTimezone(this.props.stop))
              .format('L LT z')
            : null}
        </TableCell>
      </TableRow>
    ));

    return (
      <div className="overflow-x-auto">
        <Table className="">
          <TableHeader>
            <TableRow>
              <TableHead>{t('STATUS')}</TableHead>
              <TableHead>{t('TIMESTAMP')}</TableHead>
              <TableHead>{t('ACTUAL')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>{items}</TableBody>
        </Table>
      </div>
    );
  }

  renderTimelineAuditHistory() {
    const { t, audits } = this.props;

    if (!audits?.length) {
      return <p>{t('NO_DATA_FOUND')}</p>;
    }
    const items = (audits || []).map((audit, i) => {
      return (
        <TableRow key={i}>
          <TableCell>
            {titleize(t(sluggify(audit?.meta?.stop_status))) || ''}
          </TableCell>
          <TableCell>
            {moment
              .tz(audit?.timestamp, getTimezone(this.props.stop))
              .format('L LT z')}
          </TableCell>
          <TableCell>
            {hasMetaDate(audit)
              ? moment
                .tz(audit?.meta?.date, getTimezone(this.props.stop))
                .format('L LT z')
              : null}
          </TableCell>
          <TableCell>
            <div className="flex items-center space-x-1">
              <span>{t(`${sluggify(audit?.event)}_EVENT_MESSAGE`) || ''}</span>
              {audit?.meta?.request_source && (
                <div className="w-fit">
                  <Tooltip
                    content={getStopTooltip(audit?.meta?.request_source)}
                  >
                    <InfoIcon />
                  </Tooltip>
                </div>
              )}
            </div>
          </TableCell>
          <TableCell>{audit?.meta?.reason_type || ''}</TableCell>
          <TableCell>{audit?.meta?.reason_code || ''}</TableCell>
          <TableCell>{audit?.meta?.reason_description || ''}</TableCell>
          <TableCell>{audit?.meta?.message || ''}</TableCell>
        </TableRow>
      );
    });

    return (
      <div className="overflow-x-auto">
        <Table className="">
          <TableHeader>
            <TableRow>
              <TableHead>{t('STATUS')}</TableHead>
              <TableHead>{t('TIMESTAMP')}</TableHead>
              <TableHead>{t('ACTUAL')}</TableHead>
              <TableHead>{t('STOP_ACTION')}</TableHead>
              <TableHead>{t('REASON_TYPE')}</TableHead>
              <TableHead>{t('REASON_CODE')}</TableHead>
              <TableHead>{t('REASON_DESCRIPTION')}</TableHead>
              <TableHead>{t('COMMENT')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>{items}</TableBody>
        </Table>
      </div>
    );
  }
  renderReferenceNumbers() {
    const {
      stop: { reference_numbers },
      t,
    } = this.props;

    if (!reference_numbers || reference_numbers.length === 0) {
      return <p>{t('NO_REFERENCE_NUMBERS')}</p>;
    }

    const refNumList = reference_numbers.map((refNum, i) => (
      <TableRow key={i}>
        <TableCell>{humanize(refNum.name)}</TableCell>
        <TableCell>{refNum.value}</TableCell>
      </TableRow>
    ));

    return (
      <Table className="">
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Value</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>{refNumList}</TableBody>
      </Table>
    );
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.stop &&
      this.props.stop &&
      (prevProps.stop.status !== this.props.stop.status ||
        prevProps.stop.proof_of_completion !==
        this.props.stop.proof_of_completion)
    ) {
      this.state.documentsRefetch?.();
    }
  }

  render() {
    const {
      shipment: { shipper_accessorials },
      stop,
      lastStop,
      selectedForSplit,
      t,
      yardName,
    } = this.props;

    if (!stop) return t('LOADING');

    let color = '#38495C';

    const shipmenType = this.props.shipment.shipment_type;

    if (stop.canceled || stop.attempt) color = '#d9534f';

    const {
      stop: { timeline },
    } = this.props;

    const flattened = Object.keys(timeline).map((key) => ({
      key,
      ...timeline[key],
    }));
    const sortedTimeline = filterTimelineItems(
      _.sortBy(flattened, (t) => t?.status?.code),
    );

    const stopIntent = humanize(t(sluggify(getDisplayAction(stop))));
    return (
      <>
        <div className="py-5 sm:py-6">
          <div className="grid grid-cols-12 gap-4">
            <div
              className="flex col-span-2 2xl:col-span-1"
              onClick={() => this.props.onStopSelect(stop)}
            >
              {this.props.children}
              <Node
                className=""
                color={color}
                sequence={stop.sequence}
                selectedForSplit={selectedForSplit}
                lastStop={lastStop}
              />
              <div className="ml-2 mt-2">{stopIntent}</div>
            </div>
            <div
              className="col-span-2 2xl:col-span-3"
              onMouseEnter={() => this.onMouseOver(stop.sequence, 'location')}
              onMouseLeave={() => this.onMouseOut()}
            >
              <div className="flex justify-between items-start">
                <div>
                  <DescriptionListItem
                    key="address"
                    label={yardName || humanize(getStopName(stop.location))}
                    className="mb-4"
                  >
                    {getStopAddress(stop.location)}
                  </DescriptionListItem>
                  <div>
                    {stop.contacts &&
                      stop.contacts.map((contact, i) => (
                        <div key={i} className="text-sm">
                          {getContact(contact)}
                        </div>
                      ))}
                  </div>
                  <div className="mt-2">
                    {stop.off_site && (
                      <Badge color="blue">{t('OFFSITE_STOP')}</Badge>
                    )}
                  </div>
                </div>
                <Button
                  size="icon"
                  variant="icon"
                  onClick={() => this.props.onShowEditLocationIntentModal(stop)}
                >
                  <PencilIcon className="w-4 h-4 text-gray-500" />
                </Button>
              </div>
            </div>
            {this.renderAppointmentWindow()}
            <div
              className="col-span-2"
              onMouseEnter={() => this.onMouseOver(stop.sequence, 'load')}
              onMouseLeave={() => this.onMouseOut()}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  {this.getLoadPreview(this.props.shipment)}
                </div>
                <div
                  style={{
                    display: this.showEditIcon(stop.sequence, 'load'),
                  }}
                >
                  <Button
                    variant="icon"
                    size="icon"
                    onClick={() => {
                      if (isDrayageOrRailShipment(this.props.shipment)) {
                        this.props.onShowEditEquipmentModal(stop);
                      } else {
                        this.props.onShowEditLoadsModal(stop);
                      }
                    }}
                  >
                    <PencilIcon
                      className="w-4 h-4 text-gray-500"
                      style={{
                        display: this.hasShorthaulInvoicingData(this.props)
                          ? 'none'
                          : 'auto',
                      }}
                    />
                  </Button>
                </div>
              </div>
            </div>
            <div className="col-span-2">
              {(sortedTimeline?.length ?? 0) === 0 ? (
                <span>&nbsp;</span>
              ) : (
                <Timeline>
                  {(sortedTimeline ?? []).map((timelineItem, i) => (
                    <TimelineEvent key={i} className="space-y-2">
                      <TimelineDot />
                      <DescriptionListItem label={timelineItem.status.message}>
                        {hasMetaDate(timelineItem) && (
                          <p className="mt-2 text-gray-700 text-sm">
                            {moment
                              .tz(
                                timelineItem.meta.date,
                                getTimezone(this.props.stop),
                              )
                              .format('L LT z')}
                          </p>
                        )}
                      </DescriptionListItem>
                    </TimelineEvent>
                  ))}
                </Timeline>
              )}
            </div>
            <div className="col-span-2">
              <div className="w-44">
                {this.getNextAction()}
                <DropdownMenu id="actions" className="">
                  <DropdownMenuTrigger
                    asChild
                    className="w-full mt-4"
                    disabled={this.props.actionsDisabled}
                  >
                    <Button variant="outline" className="w-full">
                      {t('MANAGE')}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem
                      onSelect={() => this.props.onActionSelect('unstarted')}
                    >
                      {t('RE_OPEN')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={isDrayageOrRailShipment(this.props.shipment)}
                      onSelect={() => this.props.onActionSelect('attempt')}
                    >
                      {t('ATTEMPT')}
                    </DropdownMenuItem>
                    {isDrayageOrRailShipment(this.props.shipment) && (
                      <DropdownMenuItem
                        onSelect={() => this.props.onActionSelect('canceled')}
                      >
                        {t('CANCEL')}
                      </DropdownMenuItem>
                    )}
                    {stop.action === 'pickup' &&
                      isDrayageOrRailShipment(this.props.shipment) && (
                        <DropdownMenuItem
                          onSelect={() =>
                            this.props.onActionSelect('add_stop_off')
                          }
                        >
                          {t('ADD_STOP_OFF')}
                        </DropdownMenuItem>
                      )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        </div>
        <Disclosure.Panel>
          <div>
            <div className="mt-4 lg:grid lg:grid-cols-12 lg:gap-4 space-x-4">
              <div className="lg:col-span-4">
                <div className="flex items-center mb-4 space-x-2">
                  <h4 className="my-0">{t('DOCUMENTS')}</h4>
                  <Tooltip content={TOOLTIPS.documents}>
                    <InfoIcon />
                  </Tooltip>
                </div>
                <Documents
                  shipment={stop}
                  isStop
                  stopShipmentId={this.props.shipment?._id}
                  showDropzone={false}
                  onDelete={(document) => {
                    console.log('Deleted Document: ', document);
                  }}
                  onUpload={(shipment, type, documents) => {
                    console.log('Uploaded Documents: ', documents);
                  }}
                  refetchRef={(documentsRefetch) =>
                    this.setDocumentsRefetchRef(documentsRefetch)
                  }
                />
              </div>
              {shipmenType === 'drayage' ? null : (
                <div className="lg:col-span-4">
                  <div className="flex items-center mb-4 space-x-2">
                    <h4 className="my-0">{t('ACCESSORIAL', { count: 3 })}</h4>
                    <Tooltip content={TOOLTIPS.accessorials}>
                      <InfoIcon />
                    </Tooltip>
                  </div>
                  <Accessorials accessorials={shipper_accessorials} />
                </div>
              )}
              <div
                className="lg:col-span-4"
                onMouseEnter={() => {
                  this.onMouseOver(
                    this.props.stop.sequence,
                    'reference_numbers',
                  );
                }}
                onMouseLeave={() => this.onMouseOut()}
              >
                <div className="flex space-x-4">
                  <div className="flex-1">
                    <div className="flex items-center mb-4 space-x-2">
                      <h4 className="my-0">{t('REFERENCE_NUMBERS')} </h4>
                      <Tooltip content={TOOLTIPS.references}>
                        <InfoIcon />
                      </Tooltip>
                    </div>
                    {this.renderReferenceNumbers()}
                  </div>
                  <div
                    style={{
                      display: this.showEditIcon(
                        stop.sequence,
                        'reference_numbers',
                      )
                        ? ''
                        : 'none',
                    }}
                  >
                    <Button
                      variant="icon"
                      size="icon"
                      onClick={() => {
                        this.props.onShowEditReferenceNumbersModal(stop);
                      }}
                    >
                      <PencilIcon className="w-4 h-4 text-gray-500" />
                    </Button>
                  </div>
                </div>
              </div>
              <div className="lg:col-span-4">
                <h4 className="my-0 mb-4">{t('TIMELINE')}</h4>
                {this.renderTimeline()}
              </div>
              <div className="col-span-12 ">
                <div className="flex items-center">
                  <h4 className="my-4 mb-4 mr-2">
                    {t('STOP_TIMELINE_HISTORY')}
                  </h4>
                  <CircleButton
                    size="small"
                    onClick={() => {
                      this.props.refreshStopsAudit &&
                        this.props.refreshStopsAudit();
                    }}
                  >
                    <RefreshIcon className="w-5 h-5" />
                  </CircleButton>
                </div>
                {this.renderTimelineAuditHistory()}
              </div>
            </div>
          </div>
        </Disclosure.Panel>
      </>
    );
  }
}

Stop.propTypes = {
  children: PropTypes.any,
  stop: PropTypes.shape({
    sequence: PropTypes.number.isRequired,
    action: PropTypes.string.isRequired,
    location: PropTypes.array.isRequired,
    contacts: PropTypes.array,
    window_start: PropTypes.string,
    window_end: PropTypes.string,
    timeline: PropTypes.object,
    canceled: PropTypes.bool,
    attempt: PropTypes.bool,
    reference_numbers: PropTypes.array,
    status: PropTypes.string,
    complete: PropTypes.bool,
    equipments: PropTypes.array,
    loads: PropTypes.array,
    full_truckload: PropTypes.bool,
  }).isRequired,
  completeActionDisabled: PropTypes.bool,
  actionsDisabled: PropTypes.bool,
  lastStop: PropTypes.bool,
  shipment: PropTypes.object,
  onActionSelect: PropTypes.func.isRequired,
  onShowEditLocationIntentModal: PropTypes.func.isRequired,
  onShowEditLoadsModal: PropTypes.func.isRequired,
  onShowEditReferenceNumbersModal: PropTypes.func.isRequired,
  onShowEditTimeWindowModal: PropTypes.func.isRequired,
  onShowEditEquipmentModal: PropTypes.func.isRequired,
  onShowStopCompleteModal: PropTypes.func.isRequired,
  onShowStopDocumentPreviewModal: PropTypes.func.isRequired,
  onShowStopArrivedModal: PropTypes.func.isRequired,
  expanded: PropTypes.bool.isRequired,
  showTotalWeight: PropTypes.bool,
};

Stop.defaultProps = {
  showTotalWeight: true,
};

export default withTranslation()(Stop);
